import { Alert, Spinner } from '@facile-it/shark-morph/components'
import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import * as React from 'react'
import { identity } from 'rxjs'
import { v1 as uuidV1 } from 'uuid'
import type { ViewDone } from '../../types/SceneViewAware'
import type { AtLeastOne } from '../../types/definition'
import type { TresourceConfig, TviewConfig } from './masterStateBuilder'

export namespace MasterState {
    export type resourceConfig = TresourceConfig<unknown, unknown, unknown, unknown, unknown, unknown, unknown>
    export type viewConfig = TviewConfig<ViewDone<unknown, unknown>, unknown>

    export interface customizationProps {
        errorMessage: React.ReactNode
        type: 'custom'
        customLoader: React.ReactChild
    }

    export interface Props {
        resourceStates?: resourceConfig[]
        viewStates?: viewConfig[]
        customization?: AtLeastOne<customizationProps>
        children: () => React.ReactNode | React.ReactNode[]
    }
}

/**
 * @internal use masterStateBuilder
 * */
export class MasterState extends React.PureComponent<MasterState.Props> {
    public static showOnlyOneLoading = null as string | null

    private uuid: string

    constructor(props: MasterState.Props) {
        super(props)

        this.uuid = uuidV1()
    }

    private retrieveStillLoading() {
        const reduceResourceStates = (stillLoading: boolean, config: MasterState.resourceConfig) =>
            stillLoading ||
            (config.isSubmitted
                ? config.resource.status === 'submitted'
                : config.resource.status === 'init' || config.resource.status === 'submitted')

        const reduceViewStates = (stillLoading: boolean, config: MasterState.viewConfig) =>
            stillLoading || config.view.status === 'pending'

        return (
            (this.props.resourceStates || []).reduce(reduceResourceStates, false) ||
            (this.props.viewStates || []).reduce(reduceViewStates, false)
        )
    }

    private retrieveError() {
        const reduceResourceStates = (error: boolean, config: MasterState.resourceConfig) =>
            error ||
            (config.resource.status === 'fail' && !config.canFail) ||
            (config.resource.status === 'rejected' && !config.canReject)

        const reduceViewStates = (error: boolean, config: MasterState.viewConfig) =>
            error || (config.view.status === 'fail' && config.canFail)

        return (
            (this.props.resourceStates || []).reduce(reduceResourceStates, false) ||
            (this.props.viewStates || []).reduce(reduceViewStates as any, false)
        )
    }

    private evaluateCustomLoader = (): React.ReactNode =>
        pipe(
            O.fromNullable(this.props.customization),
            O.chainNullableK(c => c.customLoader),
            O.fold<React.ReactChild, React.ReactChild>(() => <Spinner type="default" />, identity)
        )

    private evaluateSingleLoadind = (): React.ReactNode => {
        if (MasterState.showOnlyOneLoading === null) {
            MasterState.showOnlyOneLoading = this.uuid
        }

        return MasterState.showOnlyOneLoading === this.uuid ? this.evaluateCustomLoader() : null
    }

    render() {
        if (this.retrieveStillLoading()) {
            return pipe(
                O.fromNullable(this.props.customization?.type),
                O.filter((t): t is Extract<MasterState.customizationProps['type'], 'custom'> => t === 'custom'),
                O.map(_ => this.evaluateCustomLoader()),
                O.getOrElse(() => this.evaluateSingleLoadind())
            )
        }

        MasterState.showOnlyOneLoading = null

        if (this.retrieveError()) {
            return (
                <Alert type="danger">
                    {pipe(
                        O.fromNullable(this.props.customization),
                        O.chainNullableK(c => c.errorMessage),
                        O.match(() => "C'è qualcosa che non va. Riprova più tardi", identity)
                    )}
                </Alert>
            )
        }

        return <React.Fragment>{this.props.children()}</React.Fragment>
    }
}
